import React, { useCallback, useEffect, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Loader2, ScrollText, CalendarDays } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { toast } from 'sonner';

export default function AdminLogsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (action) params.action = action;
      if (from) params.from_ = from;
      if (to) params.to = to;
      const r = await adminApi.get('/logs', { params });
      setItems(r.data?.items || r.data || []);
      setTotal(r.data?.total ?? (r.data?.length || 0));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [action, from, to, offset]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const pages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div data-testid="admin-logs-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Activity Logs</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3">
          <Search className="w-4 h-4 text-gray-400" />
          <input data-testid="logs-action-filter" value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} placeholder="Filter by action..." className="py-2 text-sm bg-transparent focus:outline-none w-[160px]" />
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <input data-testid="logs-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} className="text-sm bg-transparent focus:outline-none" />
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <input data-testid="logs-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} className="text-sm bg-transparent focus:outline-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Admin</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">IP</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((log, i) => (
                  <tr key={log.id || i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600 text-xs">{log.admin_email || log.admin_id?.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <span className="bg-pink-50 text-pink-700 text-[10px] font-black px-2 py-0.5 rounded-full">{log.action}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {log.target_type && <span className="text-gray-400 mr-1">{log.target_type}:</span>}
                      <span className="font-mono text-[11px]">{log.target_id?.slice(0, 12)}...</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate">{log.reason || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{log.ip_address || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{dayjs(log.created_at).format('MMM D, h:mm A')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 && (
            <div className="flex flex-col items-center py-10 text-gray-400 gap-2">
              <ScrollText className="w-10 h-10" />
              <p className="text-sm text-gray-500">No log entries found</p>
            </div>
          )}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">{total} total · Page {page}/{pages}</p>
          <div className="flex gap-1">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
            <button disabled={page >= pages} onClick={() => setOffset(offset + limit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
